import { useEffect, useState } from "react";

const API_URL = import.meta.env.VITE_API_URL;

function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState("");
  const [testimonialToDelete, setTestimonialToDelete] = useState(null);
  const [testimonialAction, setTestimonialAction] = useState(null);

  const getToken = () => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken")
    );
  };

  const loadTestimonials = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      if (!token) {
        throw new Error("No admin access token found.");
      }

      const response = await fetch(`${API_URL}/api/admin/testimonials`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to load testimonials.");
      }

      setTestimonials(data.testimonials || []);
    } catch (error) {
      console.error("Failed to load testimonials:", error);
      setError(error.message || "Failed to load testimonials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  const handleDecision = async (testimonialId, action) => {
    setActionLoading(testimonialId);
    setError("");

    try {
      const token = getToken();

      if (!token) {
        throw new Error("No admin access token found.");
      }

      const response = await fetch(
        `${API_URL}/api/admin/testimonials/${testimonialId}/${action}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${action} testimonial.`);
      }

      setTestimonials((current) =>
        current.map((testimonial) =>
          testimonial.id === testimonialId ? data.testimonial : testimonial,
        ),
      );
    } catch (error) {
      console.error(`Failed to ${action} testimonial:`, error);
      setError(error.message || `Failed to ${action} testimonial.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (testimonialId) => {
    setActionLoading(testimonialId);
    setError("");

    try {
      const token = getToken();

      if (!token) {
        throw new Error("No admin access token found.");
      }

      const response = await fetch(
        `${API_URL}/api/admin/testimonials/${testimonialId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete testimonial.");
      }

      setTestimonials((current) =>
        current.filter((testimonial) => testimonial.id !== testimonialId),
      );

      setTestimonialToDelete(null);
    } catch (error) {
      console.error("Failed to delete testimonial:", error);
      setError(error.message || "Failed to delete testimonial.");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading testimonials...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Testimonial Reviews
        </h1>

        <p className="mt-1 text-sm text-gray-500">
          Review testimonial submissions before they appear publicly.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {testimonials.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="font-medium text-gray-700">No testimonials found.</p>

          <p className="mt-1 text-sm text-gray-500">
            Testimonial submissions will appear here.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {testimonial.name}
                      </h2>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          testimonial.status === "approved"
                            ? "bg-green-100 text-green-700"
                            : testimonial.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                        }`}>
                        {testimonial.status
                          ? testimonial.status.charAt(0).toUpperCase() +
                            testimonial.status.slice(1)
                          : "Pending"}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500">
                      {testimonial.email || "No email provided"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700">Rating</p>

                    <p className="text-yellow-500">
                      {"★".repeat(testimonial.rating || 0)}
                      <span className="text-gray-300">
                        {"★".repeat(Math.max(0, 5 - (testimonial.rating || 0)))}
                      </span>
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Testimonial
                    </p>

                    <p className="mt-1 whitespace-pre-wrap text-gray-700">
                      {testimonial.message}
                    </p>
                  </div>

                  <div className="text-xs text-gray-400">
                    Submitted:{" "}
                    {testimonial.createdAt
                      ? new Date(testimonial.createdAt).toLocaleString()
                      : "Unknown"}
                  </div>
                </div>

                <div className="flex gap-3 md:flex-col">
                  {testimonial.status === "pending" && (
                    <>
                      <button
                        type="button"
                        disabled={actionLoading === testimonial.id}
                        onClick={() =>
                          setTestimonialAction({
                            testimonial,
                            action: "approve",
                          })
                        }
                        className="rounded-lg bg-[#D2138C] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#b01074] disabled:cursor-not-allowed disabled:opacity-50">
                        Approve
                      </button>

                      <button
                        type="button"
                        disabled={actionLoading === testimonial.id}
                        onClick={() =>
                          setTestimonialAction({
                            testimonial,
                            action: "reject",
                          })
                        }
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                        Reject
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    disabled={actionLoading === testimonial.id}
                    onClick={() => setTestimonialToDelete(testimonial)}
                    className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {testimonialAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div
              className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
                testimonialAction.action === "approve"
                  ? "bg-green-100"
                  : "bg-yellow-100"
              }`}>
              <span
                className={`text-2xl font-bold ${
                  testimonialAction.action === "approve"
                    ? "text-green-600"
                    : "text-yellow-600"
                }`}>
                !
              </span>
            </div>

            <h2 className="text-xl font-semibold text-gray-900">
              {testimonialAction.action === "approve"
                ? "Approve Testimonial"
                : "Reject Testimonial"}
            </h2>

            <p className="mt-2 text-sm leading-6 text-gray-600">
              Are you sure you want to{" "}
              <span className="font-semibold">{testimonialAction.action}</span>{" "}
              the testimonial from{" "}
              <span className="font-semibold text-gray-900">
                {testimonialAction.testimonial.name}
              </span>
              ?
            </p>

            {testimonialAction.action === "approve" && (
              <p className="mt-2 text-sm text-gray-500">
                Once approved, this testimonial may appear publicly on the
                website.
              </p>
            )}

            {testimonialAction.action === "reject" && (
              <p className="mt-2 text-sm text-gray-500">
                The testimonial will be marked as rejected and will not appear
                publicly.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={actionLoading === testimonialAction.testimonial.id}
                onClick={() => setTestimonialAction(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                Cancel
              </button>

              <button
                type="button"
                disabled={actionLoading === testimonialAction.testimonial.id}
                onClick={async () => {
                  await handleDecision(
                    testimonialAction.testimonial.id,
                    testimonialAction.action,
                  );

                  setTestimonialAction(null);
                }}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  testimonialAction.action === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-yellow-500 hover:bg-yellow-600"
                }`}>
                {actionLoading === testimonialAction.testimonial.id
                  ? "Processing..."
                  : testimonialAction.action === "approve"
                    ? "Approve Testimonial"
                    : "Reject Testimonial"}
              </button>
            </div>
          </div>
        </div>
      )}

      {testimonialToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <span className="text-2xl font-bold text-red-600">!</span>
            </div>

            <h2 className="text-xl font-semibold text-gray-900">
              Delete Testimonial
            </h2>

            <p className="mt-2 text-sm leading-6 text-gray-600">
              Are you sure you want to delete the testimonial from{" "}
              <span className="font-semibold text-gray-900">
                {testimonialToDelete.name}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={actionLoading === testimonialToDelete.id}
                onClick={() => setTestimonialToDelete(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                Cancel
              </button>

              <button
                type="button"
                disabled={actionLoading === testimonialToDelete.id}
                onClick={() => handleDelete(testimonialToDelete.id)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                {actionLoading === testimonialToDelete.id
                  ? "Deleting..."
                  : "Delete Testimonial"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Testimonials;
